/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module media-embed/mediaembedui
 */

import {repositionContextualBalloon} from "@ckeditor/ckeditor5-image/src/image/ui/utils";
import {icons, Plugin} from 'ckeditor5/src/core';
import {ButtonView, clickOutsideHandler, ContextualBalloon, CssTransitionDisablerMixin} from 'ckeditor5/src/ui';
import CustomImageAlternativeTextFormView from "./custom-image-alternative-text-form-view";
import {getBalloonPositionData} from "./custom-image-utils";

/**
 * The media embed UI plugin.
 *
 * @extends module:core/plugin~Plugin
 */
export default class CustomImageAlternativeTextUI extends Plugin {
    /**
     * @inheritDoc
     */
    static get requires() {
        return [ContextualBalloon];
    }

    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'CustomImageAlternativeTextUI';
    }

    /**
     * @inheritDoc
     */
    init() {
        this._createButton();
        // const editor = this.editor;
        // const command = editor.commands.get('customImageAlternativeText');
        //
        // editor.ui.componentFactory.add('customImageAlternativeText', locale => {
        //     const dropdown = createDropdown(locale);
        //     const alternativeForm = new CustomImageAlternativeTextFormView(getFormValidators(editor.t), editor.locale);
        //
        //     this._setUpDropdown(dropdown, alternativeForm, command, editor);
        //     this._setUpForm(dropdown, alternativeForm, command);
        //
        //     return dropdown;
        // });
    }

    destroy() {
        super.destroy();
        // Destroy created UI components as they are not automatically destroyed (see ckeditor5#1341).
        if (this._form) {
            this._form.destroy();
        }
    }

    _createButton() {
        const editor = this.editor;
        const t = editor.t;
        editor.ui.componentFactory.add('customImageAlternativeText', locale => {
            const command = editor.commands.get('customImageAlternativeText');
            const view = new ButtonView(locale);
            view.set({
                label: t('Change image text alternative'), icon: icons.lowVision, tooltip: true
            });
            view.bind('isEnabled').to(command, 'isEnabled');
            view.bind('isOn').to(command, 'value', value => !!value);
            this.listenTo(view, 'execute', () => {
                this._showForm();
            });
            return view;
        });

    }

    _createForm() {
        const editor = this.editor;
        const view = editor.editing.view;
        const viewDocument = view.document;
        const imageUtils = editor.plugins.get('ImageUtils');
        this._balloon = this.editor.plugins.get('ContextualBalloon');
        this._form = new (CssTransitionDisablerMixin(CustomImageAlternativeTextFormView))(editor.locale);
        // Render the form so its #element is available for clickOutsideHandler.
        this._form.render();
        this.listenTo(this._form, 'submit', () => {
            editor.execute('customImageAlternativeText', {
                newValue: this._form.labeledInput.fieldView.element.value
            });
            this._hideForm(true);
        });
        this.listenTo(this._form, 'cancel', () => {
            this._hideForm(true);
        });
        // Close the form on Esc key press.
        this._form.keystrokes.set('Esc', (data, cancel) => {
            this._hideForm(true);
            cancel();
        });
        // Reposition the balloon or hide the form if an image widget is no longer selected.
        this.listenTo(editor.ui, 'update', () => {
            if (!imageUtils.getClosestSelectedImageWidget(viewDocument.selection)) {
                this._hideForm(true);
            } else if (this._isVisible) {
                repositionContextualBalloon(editor);
            }
        });
        // Close on click outside of balloon panel element.
        clickOutsideHandler({
            emitter: this._form,
            activator: () => this._isVisible,
            contextElements: () => [this._balloon.view.element],
            callback: () => this._hideForm()
        });
    }

    /**
     * Shows the {@link #_form} in the {@link #_balloon}.
     */
    _showForm() {
        if (this._isVisible) {
            return;
        }
        if (!this._form) {
            this._createForm();
        }
        const editor = this.editor;
        const command = editor.commands.get('customImageAlternativeText');
        const labeledInput = this._form.labeledInput;
        this._form.disableCssTransitions();
        if (!this._isInBalloon) {
            this._balloon.add({
                view: this._form, position: getBalloonPositionData(editor)
            });
        }
        // Make sure that each time the panel shows up, the field remains in sync with the value of
        // the command. If the user typed in the input, then canceled the balloon (`labeledInput#value`
        // stays unaltered) and re-opened it without changing the value of the command, they would see the
        // old value instead of the actual value of the command.
        // https://github.com/ckeditor/ckeditor5-image/issues/114
        labeledInput.fieldView.value = labeledInput.fieldView.element.value = command.value || '';
        this._form.labeledInput.fieldView.select();
        this._form.enableCssTransitions();
    }

    /**
     * Removes the {@link #_form} from the {@link #_balloon}.
     *
     * @param focusEditable Controls whether the editing view is focused afterwards.
     */
    _hideForm(focusEditable = false) {
        if (!this._isInBalloon) {
            return;
        }
        // Blur the input element before removing it from DOM to prevent issues in some browsers.
        // See https://github.com/ckeditor/ckeditor5/issues/1501.
        if (this._form.focusTracker.isFocused) {
            this._form.saveButtonView.focus();
        }
        this._balloon.remove(this._form);
        if (focusEditable) {
            this.editor.editing.view.focus();
        }
    }

    /**
     * Returns `true` when the {@link #_form} is the visible view in the {@link #_balloon}.
     */
    get _isVisible() {
        return !!this._balloon && this._balloon.visibleView === this._form;
    }

    /**
     * Returns `true` when the {@link #_form} is in the {@link #_balloon}.
     */
    get _isInBalloon() {
        return !!this._balloon && this._balloon.hasView(this._form);
    }
}

//____________________________________________________________________________________________//
//     _setUpDropdown(dropdown, form, command) {
//         const editor = this.editor;
//         const t = editor.t;
//         const button = dropdown.buttonView;
//         // dropdown.bind( 'isOn', 'isEnabled' ).to( command, 'value', 'isEnabled' );
//         dropdown.panelView.children.add(form);
//
//         button.set({
//             label: t('Alternative text'),
//             icon: iconsMap.get('alternative'),
//             tooltip: true,
//             withText: false
//         });
//
//
//         // Note: Use the low priority to make sure the following listener starts working after the
//         // default action of the drop-down is executed (i.e. the panel showed up). Otherwise, the
//         // invisible form/input cannot be focused/selected.
//         button.on('open', () => {
//             form.disableCssTransitions();
//
//             // Make sure that each time the panel shows up, the URL field remains in sync with the value of
//             // the command. If the user typed in the input, then canceled (`urlInputView#fieldView#value` stays
//             // unaltered) and re-opened it without changing the value of the media command (e.g. because they
//             // didn't change the selection), they would see the old value instead of the actual value of the
//             // command.
//             form.url = command.value || '';
//             form.urlInputView.fieldView.select();
//             form.enableCssTransitions();
//         }, {priority: 'low'});
//
//         dropdown.on('submit', () => {
//             if (form.isValid()) {
//                 button.set({isOn: true})
//                 editor.execute('customImageAlternativeText', form.url);
//                 editor.editing.view.focus();
//             }
//         });
//
//         dropdown.on('change:isOpen', () => form.resetFormStatus());
//         dropdown.on('cancel', () => {
//             editor.editing.view.focus();
//         });
//
//         command.on('change:value', (evt, propertyName, newValue, oldValue) => {
//             button.set({isOn: !!newValue})
//         })
//     }
//
//     /**
//      * @private
//      * @param {module:ui/dropdown/dropdownview~DropdownView} dropdown
//      * @param {module:ui/view~View} form
//      * @param {module:media-embed/mediaembedcommand~MediaEmbedCommand} command
//      */
//     _setUpForm(dropdown, form, command) {
//         form.delegate('submit', 'cancel').to(dropdown);
//         form.urlInputView.bind('value').to(command, 'value');
//
//         // Form elements should be read-only when corresponding commands are disabled.
//         form.urlInputView.bind('isReadOnly').to(command, 'isEnabled', value => !value);
//     }
// }
//
// function getFormValidators(t, registry) {
//     return [
//         form => {
//             if (!form.url.length) {
//                 return t('The must not be empty.');
//             }
//         },
//     ];
// }
